import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

import { SIGNAL_STATUS, useUpdateSignalStatus } from '@/api/churn-signals/useUpdateSignalStatus';
import { Button } from '@/component-library/primitives/Button/Button';
import { useTracking } from '@/hooks/useTracking';

interface NotRelevantSignalModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Shown in the subtitle for context */
  signalTitle: string;
  signalUuid: string;
  /** After successful dismiss — typically close parent signal modal */
  onSuccess: () => void;
}

/**
 * Second-layer modal: optional note + confirm marking a signal as not relevant (dismissed).
 */
const NotRelevantSignalModal: React.FC<NotRelevantSignalModalProps> = ({
  isOpen,
  onClose,
  signalTitle,
  signalUuid,
  onSuccess,
}) => {
  const { trackEvent } = useTracking();
  const updateStatusMutation = useUpdateSignalStatus();
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setNote('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    try {
      const trimmed = note.trim();
      await updateStatusMutation.mutateAsync({
        signalId: signalUuid,
        status: SIGNAL_STATUS.Dismissed,
        notRelevantReason: trimmed || null,
      });
      trackEvent('signal_marked_not_relevant', { signal_id: signalUuid });
      onSuccess();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full border border-custom-lightSilver"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="not-relevant-modal-title"
        aria-modal="true"
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-custom-mediumSilver">
          <div>
            <h2 id="not-relevant-modal-title" className="text-lg font-semibold text-custom-black">
              Mark as not relevant
            </h2>
            <p className="text-sm text-custom-darkSilver mt-1 line-clamp-2">{signalTitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-custom-darkSilver hover:text-custom-black shrink-0"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5">
          <label htmlFor="not-relevant-modal-note" className="text-sm font-medium text-custom-black block mb-1">
            Why is this not relevant? <span className="font-normal text-custom-darkSilver">(optional)</span>
          </label>
          <textarea
            id="not-relevant-modal-note"
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. False positive, wrong segment, already handled offline…"
            className="w-full text-sm text-custom-black border border-custom-lightSilver rounded-lg px-3 py-2 resize-y min-h-[5rem] focus:outline-none focus:ring-2 focus:ring-custom-blue/30"
          />
        </div>

        <div className="flex flex-wrap justify-end gap-2 px-5 pb-5">
          <Button type="button" variant="transparent" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={handleConfirm}
            disabled={updateStatusMutation.isLoading}
          >
            {updateStatusMutation.isLoading ? 'Saving…' : 'Confirm'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotRelevantSignalModal;
